import {
  INSTRUMENT_BATCH_TYPE_LABELS,
  instrumentBatchTypeSchema,
  type InstrumentBatch,
} from "@station/contracts";
import { Money } from "@station/core";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  StatusBadge,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Files, FilePlus2 } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { CounterpartyIdCombobox } from "../../../../features/counterparties/counterparty-combobox.js";
import { batchParty } from "../../../../features/instruments/instrument-values.js";
import { InstrumentPage, useInstrumentPermissions } from "../../../../features/instruments/shared.js";
import { LedgerStatusBadge } from "../../../../features/treasury/shared.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDate } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";

type BatchSummary = Omit<InstrumentBatch, "lines">;

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  type: instrumentBatchTypeSchema.optional().catch(undefined),
  from: date,
  to: date,
  counterpartyId: z.string().uuid().optional().catch(undefined),
  cancelled: z.enum(["1"]).optional().catch(undefined),
});
type BatchSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/cek-senet/bordrolar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: BatchesPage,
});

function BatchesPage() {
  const { t } = useTranslation();
  const permissions = useInstrumentPermissions();
  return (
    <InstrumentPage
      title={t("menu.instrumentBatches")}
      description={t("instruments.batches.description")}
      actions={
        permissions.canChange ? (
          <Button asChild>
            <Link to="/cek-senet/bordro-yeni">
              <FilePlus2 aria-hidden="true" />
              {t("menu.instrumentNewBatch")}
            </Link>
          </Button>
        ) : null
      }
    >
      {({ companyId }) => <BatchList companyId={companyId} />}
    </InstrumentPage>
  );
}

function BatchList({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const { company: _company, ...filters } = Route.useSearch();
  const setFilters = (patch: Partial<BatchSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  const query = {
    companyId,
    limit: 25,
    ...(filters.type ? { batchType: filters.type } : {}),
    ...(filters.from ? { dateFrom: filters.from } : {}),
    ...(filters.to ? { dateTo: filters.to } : {}),
    ...(filters.counterpartyId ? { counterpartyId: filters.counterpartyId } : {}),
    ...(filters.cancelled ? { includeCancelled: "true" as const } : {}),
  };
  const pages = useCursorPages(JSON.stringify(query));
  const list = useQuery({
    queryKey: queryKeys.instruments(companyId, "batches", { ...query, cursor: pages.cursor ?? null }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/instrument-batches", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
  });

  const columns = useMemo<DataTableColumn<BatchSummary>[]>(
    () => [
      {
        id: "batchNo",
        header: t("instruments.batches.batchNo"),
        hideable: false,
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <Link
              to="/cek-senet/bordrolar/$id"
              params={{ id: row.id }}
              className="font-mono text-xs font-semibold text-primary underline-offset-4 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.batchNo}
            </Link>
            {row.status === "CANCELLED" ? (
              <StatusBadge tone="neutral">{t("instruments.batches.cancelled")}</StatusBadge>
            ) : null}
          </span>
        ),
        exportValue: (row) => row.batchNo,
      },
      {
        id: "batchDate",
        header: t("instruments.fields.date"),
        cell: (row) => <span className="tabular">{formatDate(row.batchDate)}</span>,
        exportValue: (row) => formatDate(row.batchDate),
      },
      {
        id: "batchType",
        header: t("instruments.batches.type"),
        cell: (row) => INSTRUMENT_BATCH_TYPE_LABELS[row.batchType],
        exportValue: (row) => INSTRUMENT_BATCH_TYPE_LABELS[row.batchType],
      },
      {
        id: "party",
        header: t("instruments.batches.party"),
        cell: (row) => batchParty(row),
        exportValue: (row) => batchParty(row),
      },
      {
        id: "lineCount",
        header: t("instruments.batches.lineCount"),
        align: "right",
        cell: (row) => <span className="tabular">{row.lineCount}</span>,
        exportValue: (row) => row.lineCount,
      },
      {
        id: "totalAmount",
        header: t("instruments.batches.total"),
        align: "right",
        cell: (row) => <MoneyText value={row.totalAmount} className="font-medium" />,
        exportValue: (row) => Money.fromMinor(row.totalAmount).formatTr({ symbol: false }),
      },
      {
        id: "ledgerStatus",
        header: t("instruments.batches.ledgerStatus"),
        cell: (row) => <LedgerStatusBadge status={row.ledgerStatus} />,
        exportValue: (row) => row.ledgerStatus,
      },
      {
        id: "createdBy",
        header: t("instruments.batches.createdBy"),
        defaultVisible: false,
        cell: (row) => row.createdBy ?? "—",
        exportValue: (row) => row.createdBy ?? "",
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("menu.instrumentBatches")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Files />}
          title={t("instruments.batches.emptyTitle")}
          description={t("instruments.batches.emptyDescription")}
        />
      }
      filters={
        <>
          <FormField label={t("instruments.batches.type")} className="w-64">
            {(control) => (
              <Select
                {...control}
                value={filters.type ?? ""}
                onChange={(event) =>
                  setFilters({
                    type:
                      event.target.value === ""
                        ? undefined
                        : instrumentBatchTypeSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {instrumentBatchTypeSchema.options.map((type) => (
                  <option key={type} value={type}>
                    {INSTRUMENT_BATCH_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("treasury.fields.from")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={filters.from ?? null}
                onValueChange={(value) => setFilters({ from: value ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("treasury.fields.to")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={filters.to ?? null}
                onValueChange={(value) => setFilters({ to: value ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("instruments.fields.counterparty")} className="w-64">
            {(control) => (
              <CounterpartyIdCombobox
                {...control}
                companyId={companyId}
                counterpartyId={filters.counterpartyId ?? null}
                onChange={(id) => setFilters({ counterpartyId: id ?? undefined })}
              />
            )}
          </FormField>
          <Button
            variant="ghost"
            size="sm"
            className="mb-1"
            aria-pressed={Boolean(filters.cancelled)}
            onClick={() => setFilters({ cancelled: filters.cancelled ? undefined : "1" })}
          >
            {t("instruments.batches.includeCancelled")}
          </Button>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
      onExport={async (visible) => {
        const rows = await fetchAllPages(
          async (cursor) =>
            (
              await api.GET("/instrument-batches", {
                params: { query: { ...query, limit: 200, ...(cursor ? { cursor } : {}) } },
              })
            ).data!,
        );
        await exportRowsToExcel({
          fileName: "cek-senet-bordrolari",
          sheetName: t("menu.instrumentBatches"),
          columns: visible,
          rows,
        });
      }}
      onRowClick={(row) => void navigate({ to: "/cek-senet/bordrolar/$id", params: { id: row.id } })}
    />
  );
}
